import { type MouseEvent, type ReactElement, type ReactNode, useId, useState } from 'react';
import { iconSizes, r, s } from '@/theme/tokens';
import { Box, Popover } from '@/ui/primitives';
import { InfoOutlinedIcon } from '@/ui/primitives/icons';
import type { PopoverProps } from '@/ui/primitives';

type MetricInfoPopoverButtonProps = {
  ariaLabel: string;
  children: ReactNode;
};

const metricTrendCardFrameSelector = '[data-metric-trend-card-frame="true"]';
const popoverWidthPx = 420;
const popoverViewportMarginPx = 32;

type PopoverPlacement = 'below' | 'left' | 'right';

const resolveAnchorOrigin = (placement: PopoverPlacement): PopoverProps['anchorOrigin'] => {
  if (placement === 'below') return { vertical: 'bottom', horizontal: 'center' };
  if (placement === 'right') return { vertical: 'top', horizontal: 'right' };
  return { vertical: 'top', horizontal: 'left' };
};

const resolveTransformOrigin = (placement: PopoverPlacement): PopoverProps['transformOrigin'] => {
  if (placement === 'below') return { vertical: 'top', horizontal: 'center' };
  if (placement === 'right') return { vertical: 'top', horizontal: 'left' };
  return { vertical: 'top', horizontal: 'right' };
};

const resolvePopoverPlacement = (anchorElement: HTMLElement): PopoverPlacement => {
  const anchorRect = anchorElement.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const popoverWidthWithMargin = popoverWidthPx + popoverViewportMarginPx;

  if (viewportWidth <= 600) return 'below';
  if (
    anchorRect.left < popoverWidthWithMargin &&
    viewportWidth - anchorRect.right >= popoverWidthWithMargin
  ) {
    return 'right';
  }
  return 'left';
};

export const MetricInfoPopoverButton = ({
  ariaLabel,
  children,
}: MetricInfoPopoverButtonProps): ReactElement => {
  const popoverId = useId();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [popoverPlacement, setPopoverPlacement] = useState<PopoverPlacement>('left');
  const open = anchorEl !== null;

  const handleClick = (event: MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    const cardAnchor = event.currentTarget.closest(metricTrendCardFrameSelector);
    const nextAnchorEl = cardAnchor instanceof HTMLElement ? cardAnchor : event.currentTarget;
    setPopoverPlacement(resolvePopoverPlacement(nextAnchorEl));
    setAnchorEl(nextAnchorEl);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-label={ariaLabel}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? popoverId : undefined}
        onClick={handleClick}
        sx={{
          appearance: 'none',
          border: 0,
          padding: s('none'),
          margin: s('none'),
          background: 'transparent',
          color: 'inherit',
          flexShrink: 0,
          width: iconSizes.sm,
          height: iconSizes.sm,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          transform: 'translateY(-1px)',
          '&:focus-visible': {
            outline: '2px solid currentColor',
            outlineOffset: s('xxs'),
            borderRadius: r('sm'),
          },
        }}
      >
        <InfoOutlinedIcon sx={{ fontSize: 15, color: 'inherit' }} />
      </Box>
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={resolveAnchorOrigin(popoverPlacement)}
        transformOrigin={resolveTransformOrigin(popoverPlacement)}
        slotProps={{ paper: { square: false } }}
        paperSx={{
          ml:
            popoverPlacement === 'left'
              ? `calc(-1 * ${s('lg')})`
              : popoverPlacement === 'right'
                ? s('lg')
                : s('none'),
          mt: popoverPlacement === 'below' ? s('md') : `calc(-1 * ${s('3xl')})`,
          width: `min(${String(popoverWidthPx)}px, calc(100vw - ${String(popoverViewportMarginPx)}px))`,
          maxWidth: popoverWidthPx,
          borderWidth: '1px',
          borderRadius: r('lg'),
        }}
      >
        <Box sx={{ px: s('xl'), pt: s('xl'), pb: s('2xl') }}>{children}</Box>
      </Popover>
    </>
  );
};
